/**
 * DevSeedTools — sandbox seeding controls for the local-dev panel.
 *
 * The fast path out of the "wipe everything, re-run onboarding" loop:
 *  - Seed dev sandbox → builds (or refreshes) a fully-populated, writable
 *    workspace you own and drops you on its overview. No wizard.
 *  - Reset all + reseed → wipes every table, then seeds the sandbox, so the
 *    destructive reset lands in a working state instead of an empty one.
 *
 * Self-contained (no OrgContext) so it renders in both DevTools variants —
 * including the minimal one shown on /onboarding before any org exists.
 */
import * as React from 'react';
import { useAction, useMutation } from 'convex/react';
import { api } from '@/convex';
import { ACTIVE_ORG_STORAGE_KEY } from '@/hooks/useOrgContext';
import { btnStyle, dangerBtnStyle } from './devToolsStyles';

const labelStyle: React.CSSProperties = {
  borderTop: '1px solid #333',
  paddingTop: 8,
  fontSize: 10,
  color: '#666',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

type SeedResult = {
  orgId: string;
  orgSlug: string;
};

export function DevSeedTools() {
  const [status, setStatus] = React.useState('');
  const seedDevWorkspace = useMutation(api.devSeed.seedDevWorkspace);
  const resetAndReseed = useAction(api.devSeed.resetAndReseed);

  function landOnOverview(result: SeedResult) {
    localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, result.orgId);
    setStatus('Seeded — opening…');
    setTimeout(() => {
      window.location.href = `/${result.orgSlug}/overview`;
    }, 400);
  }

  async function handleSeed() {
    setStatus('Seeding dev sandbox…');
    try {
      landOnOverview((await seedDevWorkspace({})) as SeedResult);
    } catch (err) {
      setStatus(`Error: ${err instanceof Error ? err.message : 'Unknown'}`);
    }
  }

  async function handleResetAndReseed() {
    if (
      !window.confirm(
        'This WIPES every table (all orgs, repos, findings, the demo org) and reseeds a fresh dev sandbox owned by you. Continue?',
      )
    )
      return;
    setStatus('Wiping all data + reseeding…');
    try {
      const result = (await resetAndReseed()) as SeedResult;
      landOnOverview(result);
    } catch (err) {
      setStatus(`Error: ${err instanceof Error ? err.message : 'Unknown'}`);
    }
  }

  return (
    <>
      <div style={labelStyle}>Dev sandbox</div>

      <button type="button" onClick={handleSeed} style={btnStyle}>
        Seed dev sandbox → overview
      </button>

      <button
        type="button"
        onClick={handleResetAndReseed}
        style={dangerBtnStyle}
      >
        Reset all + reseed sandbox
      </button>

      {status && <div style={{ fontSize: 11, color: '#888' }}>{status}</div>}
    </>
  );
}
